import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useParams
} from "react-router-dom";
import {
  ArrowLeft,
  Save
} from "lucide-react";
import * as ownerApi from "../../api/payroll/overtimeRuleApi";
import * as portalApi from "../../api/payroll/overtimeRulePortalApi";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  Checkbox
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

export default function OvertimeRuleForm({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const base = isPortal ? "/staff-panel/overtime-rules" : "/overtime-rules";
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState({
    name: "",
    minOvertimeMinutes: 30,
    calculationType: "MULTIPLIER",
    multiplier: 1.5,
    fixedRatePerHour: 0,
    maxHoursPerDay: 4,
    isDefault: false,
    active: true,
  });
  const [loading, setLoading] = useState(false);

  const toast = useToast();

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await api.getAllOvertimeRules();
        const list = Array.isArray(res) ? res : res?.data || [];
        const found = list.find((x) => x._id === id);
        if (found) {
          setForm({
            name: found.name || "",
            minOvertimeMinutes: found.minOvertimeMinutes ?? 30,
            calculationType: found.calculationType || "MULTIPLIER",
            multiplier: found.multiplier ?? 1.5,
            fixedRatePerHour: found.fixedRatePerHour ?? 0,
            maxHoursPerDay: found.maxHoursPerDay ?? 4,
            isDefault: !!found.isDefault,
            active: found.active !== false,
          });
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Load failed");
      }
    })();
  }, [id, isPortal, api]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        minOvertimeMinutes: Number(form.minOvertimeMinutes),
        multiplier: Number(form.multiplier),
        fixedRatePerHour: Number(form.fixedRatePerHour),
        maxHoursPerDay: Number(form.maxHoursPerDay),
      };
      if (id) await api.updateOvertimeRule(id, payload);
      else await api.createOvertimeRule(payload);
      navigate(base);
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const calculationTypeOptions = [
    { label: "Multiplier of hourly rate", value: "MULTIPLIER" },
    { label: "Fixed amount per hour", value: "FIXED_PER_HOUR" },
  ];

  return (
    <div className="mx-auto max-w-xl space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="h-3.5 w-3.5" />}
          onClick={() => navigate(base)}
          className="mb-2 -ml-2"
        >
          Back to Rules
        </Button>
        <h1 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          {id ? "Edit" : "Create"} Overtime Rule
        </h1>
      </div>

      

      <Card>
        <CardHeader className="bg-neutral-50/50">
          <CardTitle>Rule Settings</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={submit} className="space-y-4">
            <Input
              label="Rule Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Standard Overtime 1.5x"
              required
            />

            <Select
              label="Calculation Type"
              options={calculationTypeOptions}
              value={form.calculationType}
              onChange={(val) => setForm({ ...form, calculationType: val })}
            />

            {form.calculationType === "MULTIPLIER" ? (
              <Input
                label="Multiplier (e.g. 1.5 = 150%)"
                type="number"
                step="0.1"
                min="1"
                value={form.multiplier}
                onChange={(e) => setForm({ ...form, multiplier: e.target.value })}
                required
              />
            ) : (
              <Input
                label="Fixed Rate Per Hour (₹)"
                type="number"
                min="0"
                value={form.fixedRatePerHour}
                onChange={(e) => setForm({ ...form, fixedRatePerHour: e.target.value })}
                required
              />
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Min OT Minutes (Threshold)"
                type="number"
                min="0"
                value={form.minOvertimeMinutes}
                onChange={(e) => setForm({ ...form, minOvertimeMinutes: e.target.value })}
              />

              <Input
                label="Max OT Hours / Day"
                type="number"
                min="0"
                value={form.maxHoursPerDay}
                onChange={(e) => setForm({ ...form, maxHoursPerDay: e.target.value })}
              />
            </div>

            <div className="space-y-3 pt-2">
              <Checkbox
                id="isDefault"
                checked={form.isDefault}
                onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                label="Set as default rule for unassigned staff"
              />

              <Checkbox
                id="active"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                label="Active Rule"
              />
            </div>

            <div className="flex items-center gap-3 border-t border-neutral-200 dark:border-neutral-800 pt-4">
              <Button
                type="submit"
                isLoading={loading}
                variant="primary"
                leftIcon={<Save className="h-4 w-4" />}
              >
                {id ? "Update Rule" : "Create Rule"}
              </Button>
              <Button type="button" onClick={() => navigate(base)} variant="ghost">
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}